import { memo, useState, useEffect, useContext } from 'react';
import axiosInstance from 'utils/axios';
import useMediaQuery from '@mui/material/useMediaQuery';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import WebStoriesIcon from '@mui/icons-material/WebStories';
import SettingsApplicationsIcon from '@mui/icons-material/SettingsApplications';
import { IconReceipt2 } from '@tabler/icons-react';
import NavItem from './NavItem';
import NavGroup from './NavGroup';
import { MenuOrientation } from 'config';
import useConfig from 'hooks/useConfig';
import { getPages } from 'menu-items/pages';  // ← Import the function
import { HORIZONTAL_MAX_ITEM } from 'config';
import { useGetMenuMaster } from 'api/menu';
import JWTContext from 'contexts/JWTContext';  // ← Import JWT context

const icons = {
  IconFAQ: HelpOutlineIcon,
  IconReceipt2: IconReceipt2,
  IconSettings: AdminPanelSettingsIcon,
  IconWebStories: WebStoriesIcon,
  IconAdvanced: SettingsApplicationsIcon,
};

function mapIcons(item) {
  if (item.icon && typeof item.icon === 'string' && icons[item.icon]) {
    const copy = { ...item, icon: icons[item.icon] };
    if (Array.isArray(copy.children)) {
      copy.children = copy.children.map(mapIcons);
    }
    return copy;
  }
  if (Array.isArray(item.children)) {
    return { ...item, children: item.children.map(mapIcons) };
  }
  return item;
}

// Último perfil (admin ou não) visto neste navegador. Usado só até as
// memberships do usuário chegarem, para o menu não piscar trocando de itens.
const ADMIN_CACHE_KEY = 'gix_menu_is_admin:' + (typeof window !== 'undefined' ? window.location.host : '');
const readCachedAdmin = () => {
  try {
    const v = window.localStorage.getItem(ADMIN_CACHE_KEY);
    return v === null ? null : v === 'true';
  } catch (e) {
    return null;
  }
};

function MenuList() {
  const { user } = useContext(JWTContext);  // ← Get user from context
  const downMD = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const {
    state: { menuOrientation }
  } = useConfig();
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster?.isDashboardDrawerOpened;
  const [items, setItems] = useState([]);
  const [selectedID, setSelectedID] = useState('');

  // Check if user is admin
  // Perfil só é "conhecido" quando as memberships do usuário já chegaram.
  const menuKnown = Array.isArray(user?.memberships) && user.memberships.length > 0 && !!user?.current_org?.id;
  const isAdminComputed = menuKnown
    ? user.memberships.some(
        (mb) =>
          mb.org_id === user.current_org.id &&
          (mb.role === 'administrator' || mb.role === 'user_manager' || mb.role === 'admin')
      )
    : null;
  const [cachedAdmin] = useState(readCachedAdmin);
  // null = ainda desconhecido e sem cache: não desenha itens (evita piscar)
  const isAdmin = menuKnown ? isAdminComputed : cachedAdmin;

  const tokenKey = 'serviceToken';

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      const token = localStorage.getItem(tokenKey);
      if (isAdmin === null) {
        if (mounted) setItems([]);
        return;
      }
      
      // Get pages with admin status
      const pagesWithAdmin = getPages(isAdmin);  // ← Pass admin status
      const staticItems = Array.isArray(pagesWithAdmin?.children) 
        ? [{ ...pagesWithAdmin, children: pagesWithAdmin.children.map(mapIcons) }]
        : [];

      if (!token) {
        if (!mounted) return;
        setItems(staticItems);
        return;
      }

      try {
        // const res = await axiosInstance.get('/menu/');
        const dynamicItems = []; // Array.isArray(res.data) ? res.data : [];
        const mappedMenuItems = [...staticItems, ...dynamicItems].map(mapIcons);
        if (!mounted) return;
        setItems(mappedMenuItems);
      } catch (err) {
        if (!mounted) return;
        setItems(staticItems);
      }
    };

    load();

    const onAuthLogin = () => { if (mounted) load(); };
    const onAuthLogout = () => {
      try {
        window.localStorage.removeItem(ADMIN_CACHE_KEY);
      } catch (e) {
        /* ignore */
      }
      if (mounted) setItems([]);
    };

    window.addEventListener('auth:login', onAuthLogin);
    window.addEventListener('auth:logout', onAuthLogout);

    return () => {
      mounted = false;
      window.removeEventListener('auth:login', onAuthLogin);
      window.removeEventListener('auth:logout', onAuthLogout);
    };
  }, [isAdmin]);  // ← Add isAdmin to dependencies

  useEffect(() => {
    if (!menuKnown) return;
    try {
      window.localStorage.setItem(ADMIN_CACHE_KEY, String(isAdminComputed));
    } catch (e) {
      /* sem storage: segue sem cache */
    }
  }, [menuKnown, isAdminComputed]);

  const isHorizontal = menuOrientation === MenuOrientation.HORIZONTAL && !downMD;
  const lastItem = isHorizontal ? HORIZONTAL_MAX_ITEM : null;

  let lastItemIndex = items.length - 1;
  let remItems = [];
  let lastItemId;

  if (lastItem && lastItem < items.length) {
    lastItemId = items[lastItem - 1]?.id;
    lastItemIndex = lastItem - 1;
    remItems = items.slice(lastItem - 1, items.length).map((item) => ({
      title: item.title,
      elements: item.children,
      icon: item.icon,
      ...(item.url && { url: item.url })
    }));
  }

  const validItems = items
    .slice(0, lastItemIndex + 1)
    .filter(
      item =>
        item &&
        typeof item === "object" &&
        !Array.isArray(item) &&
        item.id &&
        item.type
    );

  const navItems = validItems.map((item, index) => {
    const key = item.id ?? item.title ?? `menu-fallback-key-${index}`;

    switch (item.type) {
      case 'group':
        if (item.url && item.id !== lastItemId) {
          return (
            <List key={key}>
              <NavItem item={item} level={1} isParents setSelectedID={() => setSelectedID('')} />
              {false && !isHorizontal && index !== 0 && <Divider sx={{ py: 0.5 }} />}
            </List>
          );
        }
        return (
          <NavGroup
            key={key}
            setSelectedID={setSelectedID}
            selectedID={selectedID}
            item={item}
            lastItem={lastItem}
            remItems={remItems}
            lastItemId={lastItemId}
          />
        );
      case 'item':
        return (
          <List key={key}>
            <NavItem item={item} level={1} setSelectedID={() => setSelectedID(item.id)} />
            {false && !isHorizontal && <Divider sx={{ py: 0.5 }} />}
          </List>
        );
      case 'collapse':
        return (
          <NavGroup
            key={key}
            setSelectedID={setSelectedID}
            selectedID={selectedID}
            item={item}
            lastItem={lastItem}
            remItems={remItems}
            lastItemId={lastItemId}
          />
        );
      default:
        return null;
    }
  });

  return !isHorizontal ? (
    <Box {...(drawerOpen && { sx: { mt: 1.5 } })}>{navItems}</Box>
  ) : (
    <>{navItems}</>
  );
}

export default memo(MenuList);